import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { usePagination } from '@crehana/react-hooks';
import { notEmpty } from '@crehana/utils';
import { useRouter } from 'next/router';

import type { TeamTypeEnum } from '@/shared/types/graphql/globals.v4.b2b';
import type { FilterSelectOption } from '@/views/AdminDashboard/components/FilterSelect/types';
import useDebounce from '@/views/AdminDashboard/hooks/useDebounce';

import GET_ORGANIZATION_TRACKS from './graphql/OrganizationTracks.v4.b2b.graphql';
import GET_ORGANIZATION_TRACKS_TOTALS from './graphql/OrganizationTracksTotals.v4.b2b.graphql';
import GET_ORGANIZATION_TRACKS_WITH_COURSE_PREVIEWS from './graphql/OrganizationTracksWithCoursePreviews.v4.b2b.graphql';
import type {
  OrganizationTracksTotals,
  OrganizationTracksTotalsVariables,
} from './graphql/types/OrganizationTracksTotals';
import {
  type TracksNode,
  type TracksVariables,
  tracksQueryTypeEnum,
  type UseOrganizationTracksProps,
} from './types';

const ITEMS_PER_FETCH = 5;

const useOrganizationTracks = ({
  queryType = tracksQueryTypeEnum.BASE,
  batchSize,
  filterOptions = [],
  selectedTracks: organizationTeamIds = [],
}: UseOrganizationTracksProps = {}) => {
  const router = useRouter();
  const { slug } = router.query as { slug: string };
  const { debounceValue: searchKey, handleChange } = useDebounce();
  const [filter, setFilter] = useState<FilterSelectOption>(filterOptions[0]);
  const [options, setOptions] = useState(filterOptions);

  const trackType =
    filter?.value.toString() !== ''
      ? (filter?.value as TeamTypeEnum)
      : undefined;

  const query =
    queryType === tracksQueryTypeEnum.BASE
      ? GET_ORGANIZATION_TRACKS
      : GET_ORGANIZATION_TRACKS_WITH_COURSE_PREVIEWS;

  const { data, loading, error, refetch } = useQuery<
    TracksNode,
    TracksVariables
  >(query, {
    variables: {
      slug,
      first: batchSize || ITEMS_PER_FETCH,
      after: '',
      searchKey,
      trackType,
      organizationTeamIds,
    },
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
  });
  const { data: rawData } = useQuery<
    OrganizationTracksTotals,
    OrganizationTracksTotalsVariables
  >(GET_ORGANIZATION_TRACKS_TOTALS, {
    variables: {
      slug,
      searchKey,
      trackType,
      organizationTeamIds,
    },
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    changePage(1);
  }, [searchKey]);

  const items =
    data?.organizationTeams?.edges.map(el => el?.node).filter(notEmpty) || [];

  const allIds =
    rawData?.organizationTeams?.edges
      .map(el => el?.node.originalId)
      .filter(notEmpty) || [];

  const pageIds =
    data?.organizationTeams?.edges
      .map(el => el?.node.originalId)
      .filter(notEmpty) || [];

  const totalItems = data?.organizationTeams?.totalCount || 0;

  const { currentPage, onPageChange, totalPages } = usePagination(
    batchSize || ITEMS_PER_FETCH,
    {
      refetch,
      totalElements: totalItems,
    },
  );

  const changePage = useCallback(
    (page: number) => {
      onPageChange(page);
    },
    [onPageChange],
  );

  return {
    data,
    handleChange,
    currentPage,
    items,
    isLoading: loading,
    error,
    totalPages,
    totalItems,
    allIds,
    pageIds,
    searchKey,
    changePage,
    filter,
    setFilter,
    options,
    setOptions,
  };
};

export default useOrganizationTracks;
